import {
    ZodArray,
    ZodDiscriminatedUnion,
    ZodNullable,
    ZodObject,
    ZodOptional,
    ZodUnion,
    type ZodType,
} from 'zod';
import * as Sentry from '@sentry/vue';
import {debug} from '../../shared/helpers/debug';
import {useToast} from '../../shared/components/toast/useToast';

export function parseZodSchema<TOutput, TData>(
    // The type constraint prevents accidentally passing a zod schema as data parameter
    data: TData extends ZodType<TOutput> ? never : unknown,
    schema: ZodType<TOutput>
): TOutput {
    const result = schema.safeParse(data);
    if (!result.success) {
        debug.error(result.error);

        if (import.meta.env.DEV) {
            useToast().error('Error parsing data', {
                detail: result.error.message,
            });
            throw result.error;
        }

        Sentry.captureException(result.error);

        try {
            // try to parse as many properties as possible
            return recursivelyParse(data, schema as ZodType) as TOutput;
        } catch (e) {
            Sentry.captureException(e);
            return data as TOutput;
        }
    }

    return result.data as TOutput;
}

function recursivelyParse(data: unknown, schema: ZodType): unknown {
    if (schema instanceof ZodObject && typeof data === 'object' && data !== null) {
        const shape = schema.shape;
        const result: Record<string, unknown> = {};

        for (const key in shape) {
            const subSchema = shape[key];
            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const parsed = recursivelyParse((data as any)[key], subSchema);
                if (parsed !== undefined) {
                    result[key] = parsed;
                }
            } catch {
                // Ignore error
            }
        }

        return result;
    }

    if (schema instanceof ZodArray && Array.isArray(data)) {
        const elementSchema = schema.element;
        return data
            .map(item => {
                try {
                    return recursivelyParse(item, elementSchema as ZodType);
                } catch {
                    return undefined;
                }
            })
            .filter(item => item !== undefined);
    }

    if (schema instanceof ZodOptional || schema instanceof ZodNullable) {
        if (data === undefined || data === null) return data;
        return recursivelyParse(data, schema.unwrap() as ZodType);
    }

    if (schema instanceof ZodUnion || schema instanceof ZodDiscriminatedUnion) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const option of 'options' in schema ? schema.options : (schema as any)._def.options) {
            const result = option.safeParse(data);
            if (result.success) return result.data;
        }
        throw new Error('Union schema: all options failed');
    }

    const result = schema.safeParse(data);
    if (result.success) {
        return result.data;
    }

    return data;
}
